





















































































































































































































































import { Appointment } from '@/shared/appointments/appointment';
import { AppointmentOutcome } from '@/shared/appointments/appointment-outcome';
import { AppointmentOutcomeProduct } from '@/shared/appointments/appointment-outcome-product';
import { AppointmentProductPaymentDetails, createAppointmentProductPaymentDetails } from '@/shared/appointments/appointment-product-payment-details';
import { AppointmentPaymentStatusType } from '@/shared/types/appointment-payment-status-type';
import { formatCurrentDate, formatDate } from '@/shared/utilities/date-utilities';
import debounce from 'lodash.debounce';
import { DateTime } from 'luxon';
import { Validator } from 'vee-validate';
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const typesModule = namespace('types');
const userModule = namespace('user');

@Component
export default class AppointmentProductDetails extends Vue {
    @typesModule.Getter('appointmentPaymentStatusTypes') private appointmentPaymentStatusTypes!: Array<AppointmentPaymentStatusType>;
    @userModule.Getter('isInternalKeyStakeholder') private isInternalKeyStakeholder!: boolean;
    @userModule.Getter('isHeadOfDepartment') private isHeadOfDepartment!: boolean;

    @Inject('$validator') public $validator!: Validator;
    @Prop({ required: true }) private product!: AppointmentOutcomeProduct;
    @Prop({ required: true }) private appointment!: Appointment;
    @Prop({ required: false, default: false }) private isEdit!: boolean;

    private numberOfPayments: number | null = this.product.paymentDetails.length ? this.product.paymentDetails.length : null;
    private paymentTotalValidation: string | null = null;

    private originalAppointmentOutcome: AppointmentOutcome | null = this.appointment.outcome == null ? null : { ...this.appointment.outcome, products: [...this.appointment.outcome.products] };

    private get signingDate(): string | Date | null {
        return this.product.paymentDetails.length ? this.product.paymentDetails[0].signingDate : null;
    }

    private set signingDate(value: string | Date | null) {
        for (const payment of this.product.paymentDetails) {
            payment.signingDate = value;
        }
    }

    private get paymentDetails(): Array<AppointmentProductPaymentDetails> {
        if (!this.product.paymentDetails.length) {
            return [];
        }

        const paymentDetails = this.product.paymentDetails.sort((p1, p2) => {
            if (p1.index > p2.index) { return 1; }
            if (p1.index < p2.index) { return -1; }
            return 0;
        });

        return paymentDetails;
    }

    private get productPaymentStatusTypes(): Array<AppointmentPaymentStatusType> {
        const paymentStatuses = this.isEdit
            ? this.appointmentPaymentStatusTypes.filter((type) => !type.isArchived)
            : this.appointmentPaymentStatusTypes.filter((type) => !type.isArchived && !type.isDebtor && !type.isNoLongerProceeding && !type.isComplete);

        return this.isInternalKeyStakeholder || this.isHeadOfDepartment ? paymentStatuses : paymentStatuses.filter((type) => !type.isAdminOnly);
    }

    private get partPaymentStatusTypes(): Array<AppointmentPaymentStatusType> {
        const paymentStatuses = this.isEdit
            ? this.appointmentPaymentStatusTypes.filter((type) => !type.isArchived && !type.isSigningPending && !type.isSigningBooked)
            : this.appointmentPaymentStatusTypes.filter((type) => !type.isArchived && !type.isSigningPending && !type.isSigningBooked && !type.isDebtor && !type.isNoLongerProceeding && !type.isComplete);

        return (this.isInternalKeyStakeholder || this.isHeadOfDepartment ? paymentStatuses : paymentStatuses.filter((type) => !type.isAdminOnly))
            .map((type) => {
                return { ...type, value: type.isPartPayment ? 'Pending' : type.value };
            });
    }

    private get showAlbReference(): boolean {
        return this.appointment.isSimpsonsSolicitors != null ? this.appointment.isSimpsonsSolicitors : false;
    }

    private get showSigningDate(): boolean {
        if (this.product.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);
        return appointmentPaymentStatusType != null && !appointmentPaymentStatusType.isSigningPending;
    }

    private get showPartPayment(): boolean {
        if (this.product.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);
        return appointmentPaymentStatusType != null && appointmentPaymentStatusType.isPartPayment;
    }

    private get canEditInstructedDate(): boolean {
        if (this.isInternalKeyStakeholder || !this.isEdit || this.originalAppointmentOutcome == null) {
            return true;
        }

        const originalProduct = this.originalAppointmentOutcome.products.find((product) => product.productTypeId === this.product.productTypeId);
        return originalProduct == null ? false : originalProduct.instructedDate == null;
    }

    private get canEditInstructedValue(): boolean {
        if (this.isInternalKeyStakeholder || !this.isEdit || this.originalAppointmentOutcome == null) {
            return true;
        }

        const originalProduct = this.originalAppointmentOutcome.products.find((product) => product.productTypeId === this.product.productTypeId);
        return originalProduct == null ? false : originalProduct.instructedValue == null;
    }

    private get canEditProductPaymentStatus(): boolean {
        if (this.isInternalKeyStakeholder || !this.isEdit || this.originalAppointmentOutcome == null) {
            return true;
        }

        const originalProduct = this.originalAppointmentOutcome.products.find((product) => product.productTypeId === this.product.productTypeId);

        if (originalProduct == null) {
            return false;
        }

        if (originalProduct.paymentDetails == null || !originalProduct.paymentDetails.length) {
            return true;
        }

        if (originalProduct.paymentDetails.length > 1) {
            return false;
        }

        const originalPayment = originalProduct.paymentDetails[0];
        const originalPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === originalPayment.paymentStatusId);
        return originalPaymentStatusType == null ? false : !originalPaymentStatusType.isAdminOnly;
    }

    private get canEditNumberOfPayments(): boolean {
        if (this.isInternalKeyStakeholder || !this.isEdit || this.originalAppointmentOutcome == null) {
            return true;
        }

        const originalProduct = this.originalAppointmentOutcome.products.find((product) => product.productTypeId === this.product.productTypeId);

        if (originalProduct == null) {
            return false;
        }

        return originalProduct.paymentDetails == null || !originalProduct.paymentDetails.length ? true : originalProduct.paymentDetails.every((product) => product.value == null);
    }

    private get isSigningDateRequired(): boolean {
        if (this.product.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);
        return appointmentPaymentStatusType != null && appointmentPaymentStatusType.isSigningBooked;
    }

    private get productPaymentStatusTypeDescription(): string {
        if (this.product.paymentStatusId == null) {
            return '';
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);
        return appointmentPaymentStatusType == null ? '' : appointmentPaymentStatusType.value;
    }

    private get appointmentDate(): string | Date | null {
        return formatDate(this.appointment.date, true);
    }

    private get appointmentDateText(): string {
        const appointmentDateTime = this.appointment.date! instanceof Date
            ? DateTime.fromJSDate(this.appointment.date!, { zone: 'UTC' })
            : DateTime.fromISO(this.appointment.date!, { zone: 'UTC' });

        return appointmentDateTime.minus({ hours: appointmentDateTime.hour, minutes: appointmentDateTime.minute }).toFormat('dd/MM/yyyy');
    }

    private get instructedDate(): string | Date | null {
        return formatDate(this.product.instructedDate != null ? this.product.instructedDate : this.appointment.date, true);
    }

    private get instructedDateText(): string {
        const baseDate = this.product.instructedDate != null ? this.product.instructedDate : this.appointment.date;
        const instructedDateTime = baseDate instanceof Date
            ? DateTime.fromJSDate(baseDate, { zone: 'UTC' })
            : DateTime.fromISO(baseDate!, { zone: 'UTC' });

        return instructedDateTime.minus({ hours: instructedDateTime.hour, minutes: instructedDateTime.minute }).toFormat('dd/MM/yyyy');
    }

    @Watch('product', { deep: true })
    private onProductChanged = debounce(async () => {
        await this.validateProduct();
    }, 100);

    private showPaymentAmount(payment: AppointmentProductPaymentDetails): boolean {
        if (this.product.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);
        return appointmentPaymentStatusType != null && !appointmentPaymentStatusType.isSigningPending && !appointmentPaymentStatusType.isSigningBooked;
    }

    private showPaymentBankedDate(payment: AppointmentProductPaymentDetails): boolean {
        if (payment.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);

        if (appointmentPaymentStatusType == null || appointmentPaymentStatusType.isSigningPending || appointmentPaymentStatusType.isSigningBooked || appointmentPaymentStatusType.isPartPayment) {
            return false;
        }

        return appointmentPaymentStatusType.isDebtor || appointmentPaymentStatusType.isNoLongerProceeding || appointmentPaymentStatusType.isWrittenOff ? payment.bankedDate != null : true;
    }

    private showPaymentClearedDate(payment: AppointmentProductPaymentDetails): boolean {
        if (payment.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);
        return appointmentPaymentStatusType != null && appointmentPaymentStatusType.isCleared;
    }

    private showPaymentDebtorDate(payment: AppointmentProductPaymentDetails): boolean {
        if (payment.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);
        return appointmentPaymentStatusType != null && appointmentPaymentStatusType.isDebtor;
    }

    private showPaymentNoLongerProceedingDate(payment: AppointmentProductPaymentDetails): boolean {
        if (payment.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);
        return appointmentPaymentStatusType != null && appointmentPaymentStatusType.isNoLongerProceeding;
    }

    private showPaymentWrittenOffDate(payment: AppointmentProductPaymentDetails): boolean {
        if (payment.paymentStatusId == null) {
            return false;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);
        return appointmentPaymentStatusType != null && appointmentPaymentStatusType.isWrittenOff;
    }

    private canEditPaymentDetails(payment: AppointmentProductPaymentDetails): boolean {
        if (this.isInternalKeyStakeholder || !this.isEdit || this.originalAppointmentOutcome == null) {
            return true;
        }

        const originalProduct = this.originalAppointmentOutcome.products.find((product) => product.productTypeId === this.product.productTypeId);

        if (originalProduct == null) {
            return false;
        }

        if (originalProduct.paymentDetails == null || !originalProduct.paymentDetails.length) {
            return true;
        }

        const originalPayment = originalProduct.paymentDetails.find((p) => p.index === payment.index);

        if (originalPayment == null) {
            return true;
        }

        const originalPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === originalPayment.paymentStatusId);

        return originalPaymentStatusType != null && !originalPaymentStatusType.isAdminOnly;
    }

    private canEditCompletedDate(payment: AppointmentProductPaymentDetails): boolean {
        if (this.isInternalKeyStakeholder || !this.isEdit || this.originalAppointmentOutcome == null) {
            return true;
        }

        if (!this.isHeadOfDepartment) {
            return false;
        }

        const originalProduct = this.originalAppointmentOutcome.products.find((product) => product.productTypeId === this.product.productTypeId);

        if (originalProduct == null) {
            return false;
        }

        if (originalProduct.paymentDetails == null || !originalProduct.paymentDetails.length) {
            return true;
        }

        const originalPayment = originalProduct.paymentDetails.find((p) => p.index === payment.index);

        if (originalPayment == null) {
            return true;
        }

        const originalPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === originalPayment.paymentStatusId);

        return originalPaymentStatusType != null && !originalPaymentStatusType.isComplete;
    }

    private partPaymentStatusTypeDescription(payment: AppointmentProductPaymentDetails): string {
        if (payment.paymentStatusId == null) {
            return '';
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);
        return appointmentPaymentStatusType == null ? '' : (appointmentPaymentStatusType.isPartPayment ? 'Pending' : appointmentPaymentStatusType.value);
    }

    private bankedDate(payment: AppointmentProductPaymentDetails): string | Date | null {
        if (payment.bankedDate == null) {
            return this.instructedDate;
        }

        return formatDate(payment.bankedDate, true);
    }

    private bankedDateText(payment: AppointmentProductPaymentDetails): string {
        if (payment.bankedDate == null) {
            return this.instructedDateText;
        }

        const bankedDateTime = payment.bankedDate instanceof Date
            ? DateTime.fromJSDate(payment.bankedDate, { zone: 'UTC' })
            : DateTime.fromISO(payment.bankedDate!, { zone: 'UTC' });

        return bankedDateTime.minus({ hours: bankedDateTime.hour, minutes: bankedDateTime.minute }).toFormat('dd/MM/yyyy');
    }

    private onProductPaymentStatusChanged(): void {
        const existingSigningDate = this.product.paymentDetails.length ? this.product.paymentDetails[0].signingDate : null;
        const existingValue = this.product.paymentDetails.length ? this.product.paymentDetails[0].value : null;
        const existingBankedDate = this.product.paymentDetails.length ? this.product.paymentDetails[0].bankedDate : null;

        this.product.paymentDetails = [];

        if (this.product.paymentStatusId == null) {
            return;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);

        if (appointmentPaymentStatusType == null) {
            return;
        }

        this.numberOfPayments = appointmentPaymentStatusType.isPartPayment ? 2 : 1;

        for (let index = 0; index < this.numberOfPayments; index++) {
            const payment = createAppointmentProductPaymentDetails(appointmentPaymentStatusType, index);
            payment.signingDate = appointmentPaymentStatusType.isSigningPending ? null : existingSigningDate;
            this.product.paymentDetails.push(payment);
        }

        if (appointmentPaymentStatusType.isSigningPending || appointmentPaymentStatusType.isSigningBooked || appointmentPaymentStatusType.isPartPayment) {
            return;
        }

        this.product.paymentDetails[0].value = existingValue;
        this.product.paymentDetails[0].bankedDate = existingBankedDate;
        this.product.paymentDetails[0].debtorDate = appointmentPaymentStatusType.isDebtor ? formatCurrentDate() : null;
        this.product.paymentDetails[0].noLongerProceedingDate = appointmentPaymentStatusType.isNoLongerProceeding ? formatCurrentDate() : null;
        this.product.paymentDetails[0].writtenOffDate = appointmentPaymentStatusType.isWrittenOff ? formatCurrentDate() : null;
    }

    private onNumberOfPaymentsChanged(): void {
        const existingSigningDate = this.product.paymentDetails.length ? this.product.paymentDetails[0].signingDate : null;

        this.product.paymentDetails = [];

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === this.product.paymentStatusId);

        if (appointmentPaymentStatusType == null) {
            return;
        }

        for (let index = 0; index < this.numberOfPayments!; index++) {
            const payment = createAppointmentProductPaymentDetails(appointmentPaymentStatusType, index);
            payment.signingDate = existingSigningDate;
            this.product.paymentDetails.push(payment);
        }
    }

    private onPartPaymentStatusChanged(payment: AppointmentProductPaymentDetails): void {
        if (payment.paymentStatusId == null) {
            return;
        }

        const appointmentPaymentStatusType = this.appointmentPaymentStatusTypes.find((type) => type.id === payment.paymentStatusId);

        if (appointmentPaymentStatusType == null) {
            return;
        }

        if (appointmentPaymentStatusType.isPartPayment) {
            payment.bankedDate = null;
        }

        payment.debtorDate = appointmentPaymentStatusType.isDebtor ? formatCurrentDate() : null;
        payment.noLongerProceedingDate = appointmentPaymentStatusType.isNoLongerProceeding ? formatCurrentDate() : null;
        payment.writtenOffDate = appointmentPaymentStatusType.isWrittenOff ? formatCurrentDate() : null;
    }

    private async validateProduct(): Promise<boolean> {
        await this.$validator.validateAll('product-details');

        this.paymentTotalValidation = null;

        if (Math.abs(this.product.instructedValue!) > 0 && this.product.paymentDetails.length > 1 && this.product.paymentDetails.every((payment) => Math.abs(payment.value!) > 0)) {
            const paymentTotal = this.product.paymentDetails.reduce((sum, payment) => sum + (Math.abs(payment.value!) * 100), 0) / 100;

            if (Math.abs(this.product.instructedValue!) < Math.abs(paymentTotal)) {
                this.paymentTotalValidation = 'The total payment amount cannot exceed the instructed value.';
            } else if (Math.abs(this.product.instructedValue!) > Math.abs(paymentTotal)) {
                this.paymentTotalValidation = 'The total payment amount cannot be less than the instructed value.';
            }
        }

        this.$emit('product-changed', { productTypeId: this.product.productTypeId, errors: this.$validator.errors.any('product-details') || this.paymentTotalValidation != null });
        return !this.$validator.errors.any('product-details') && this.paymentTotalValidation == null;
    }
}
